import React, { useState, useRef, ReactElement } from "react";

import ReactCrop, { centerCrop, makeAspectCrop, Crop, PixelCrop } from "react-image-crop";
import { canvasPreview } from "./CanvasPreview";
import { useDebounceEffect } from "./UseDebounceEffect";

import "react-image-crop/dist/ReactCrop.css";
import { Button, Center } from "@chakra-ui/react";
import { Hide } from "../../common/Hide";

// This is to demonstate how to make and center a % aspect crop
// which is a bit trickier so we use some helper functions.
function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

interface Props {
  setImage: (image: Blob) => void;
  setClose: () => void;
  background: string;
}

export function ImageModal({ setImage, setClose, background }: Props): ReactElement {
  const [imgSrc, setImgSrc] = useState("");
  const imgRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const canvas = useRef<HTMLCanvasElement>(null);
  const scale = 1;
  const rotate = 0;
  const aspect = 16 / 9;

  function onSelectFile(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      setCrop(undefined); // Makes crop preview update between images.
      const reader = new FileReader();
      reader.addEventListener("load", () => setImgSrc(reader.result.toString() || ""));
      reader.readAsDataURL(e.target.files[0]);
    }
  }

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    if (aspect) {
      const { width, height } = e.currentTarget;
      setCrop(centerAspectCrop(width, height, aspect));
    }
  }

  useDebounceEffect(
    async () => {
      if (completedCrop?.width && completedCrop?.height && imgRef.current && canvas.current) {
        // We use canvasPreview as it's much faster than imgPreview.
        canvasPreview(imgRef.current, canvas.current, completedCrop, scale, rotate);
      }
    },
    100,
    [completedCrop, scale, rotate]
  );

  function uploadCroppedImage() {
    canvas.current.toBlob((blob) => setImage(blob));
    setClose();
  }

  return (
    <div className="App">
      <Center>
        <input type="file" accept="image/*" onChange={onSelectFile} />
      </Center>
      <Center>
        <Hide hide={!imgSrc}>
          <Button variant="red" onClick={uploadCroppedImage} mb="0.5vh">
            Upload
          </Button>
        </Hide>
      </Center>
      {Boolean(imgSrc) && (
        <ReactCrop
          crop={crop}
          onChange={(_, percentCrop) => setCrop(percentCrop)}
          onComplete={(c) => setCompletedCrop(c)}
          aspect={aspect}
        >
          <img
            ref={imgRef}
            alt="Crop me"
            src={imgSrc}
            style={{ transform: `scale(${scale}) rotate(${rotate}deg)` }}
            onLoad={onImageLoad}
          />
        </ReactCrop>
      )}
      <Center>
        <Hide hide={!imgSrc}>
          <Button background={background} onClick={uploadCroppedImage}>
            Upload
          </Button>
        </Hide>
      </Center>
      <div>
        {Boolean(completedCrop) && (
          <canvas
            ref={canvas}
            style={{
              display: "none",
            }}
          />
        )}
      </div>
    </div>
  );
}
